import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar'; // adjust path
import {
  FaDownload,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaCheck
} from 'react-icons/fa';
import PurchaseOrderFormModal from './PurchaseOrderFormModal'; // Import the modal component
import PurchaseOrderViewModal from './PurchaseOrderViewModal';
import PDFDesigner from '../components/pdfDesigner'; // adjust path
import axios from 'axios'; // make sure axios is imported
import { useNavigate } from 'react-router-dom';

import { useScreenPermissions } from '../components/useScreenPermissions';
const API_URL = '/api';

// Colors
const COLORS = {
  ANGEL: '#EAF9FB',      // screen background (light)
  THALASSA: '#D5F0F5',   // table header background (slightly darker)
  TABLE_BODY: '#FFFFFF', // table body white
  SECTION_TITLE_BG: '#FFFFFF', // block behind the "Purchase Orders" section title (white per request)
  SECTION_TITLE_TEXT: '#0f2b36', // dark text for section title block
  HEADER_TEXT_WHITE: '#ffffffff', // white text for the table header row
  DIVIDER: '#E6EEF2',    // divider color (same as search block border)
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const PurchaseOrderItems = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
  const [selectedPOForPDF, setSelectedPOForPDF] = useState(null);

  const [skippedItems, setSkippedItems] = useState([]); // store skipped items
const [showSkippedDetails, setShowSkippedDetails] = useState(false); // toggle for skipped items


  const [selectedPO, setSelectedPO] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  const [stats, setStats] = useState({
    totalPOs: 0,
    approvedPOs: 0,
    pendingPOs: 0,
    createdThisMonth: 0,
  });
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [totalPages, setTotalPages] = useState(0); // New state for server-side total pages

    // --- ADD THESE THREE NEW STATE VARIABLES ---
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [syncData, setSyncData] = useState(null);

  // Fetch purchase orders
  useEffect(() => {
    fetchPurchaseOrders(1);
  }, []);

  // Updated fetch function to support server-side pagination and searching
  const fetchPurchaseOrders = async (page = 1) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page,
        limit: itemsPerPage,
        search: searchTerm,
        status: status,
        sortBy: sortConfig.key || 'PurchaseOrderDate',
        sortOrder: sortConfig.direction || 'desc'
      });

      const response = await fetch(`${API_URL}/purchase-order-filter?${params.toString()}`, {
         headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch purchase orders');

      const data = await response.json();
      
      // Set data from server (already paginated and filtered)
      setPurchaseOrders(data.purchaseOrders);
      
      // Update stats from server response
      setStats(data.stats);
      
      // Update pagination info
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);

      // Note: calculateStats is no longer needed here as backend handles it, 
      // but function definition is kept below as per requirements.
      
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      alert('Error loading purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const downloadPOPDF = async (poNumber) => {
    try {
      const response = await axios.get(`${API_URL}/designs/Purchase%20Order/pdf/po/${poNumber}`, {
        responseType: 'arraybuffer', // important for PDF
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `PO_${poNumber}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(`Failed to download PDF: ${error.message}`);
    }
  };

  const calculateStats = (pos) => {
    // Kept for reference, logic now handled by backend
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let totalPOs = pos.length;
    let approvedPOs = 0;
    let pendingPOs = 0;
    let createdThisMonth = 0;

    pos.forEach((po) => {
      // Count approved/pending based on is_active field
      if (po.is_active === 1 || po.is_active === true) {
        approvedPOs++;
      } else {
        pendingPOs++;
      }

      // Check if created this month
      const poDate = new Date(po.PurchaseOrderDate);
      if (poDate.getMonth() === currentMonth && poDate.getFullYear() === currentYear) {
        createdThisMonth++;
      }
    });

    setStats({
      totalPOs,
      approvedPOs,
      pendingPOs,
      createdThisMonth,
    });
  };

  const handleDownloadCSV = () => {
    // Create CSV content
    let csvContent = 'PO Number,PO Date,Vendor Name,Total Items,Total Quantity,Total Amount,Status\n';

     purchaseOrders.forEach((po) => {
      // CHANGED: Use master data columns directly
      const locationCode = po.LocationCode || '';
      const branchCode = po.BranchCode || '';
      
       csvContent += `${po.PurchaseOrderNumber},${po.PurchaseOrderDate},${po.vendor_name || ''},${locationCode},${branchCode}\n`;
    });

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'purchase_orders.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreate = () => {
    setSelectedPO(null);
    setShowModal(true);
  };

   const handleSync = async () => {
  if (isSyncing) return;
  setIsSyncing(true);

  try {
    const res = await fetch(`${API_URL}/mapping/bc-purchase-orders/sync`, { method: 'GET' , headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    const data = await res.json();

    if (data.success) {
      setSyncData({
        total: data.recordCount,
        inserted: data.inserted,
        updated: data.updated,
      });

      setSkippedItems(data.skipped || []); // store skipped items from API
      setShowSyncSuccess(true);

      fetchPurchaseOrders(); // refresh list
    } else {
      alert(`Sync Failed: ${data.message || 'Unknown error'}`);
    }
  } catch (error) {
    console.error("Sync Error:", error);
    alert("An error occurred while syncing purchase orders.");
  } finally {
    setIsSyncing(false);
  }
};


  const handleEdit = (po) => {
    setSelectedPO(po);
    setShowModal(true);
  };

  const handleView = (po) => {
   setSelectedPO(po.PurchaseOrderNumber);
    setViewModalOpen(true);
  };

    const handleLogout = () => {
    navigate('/login');
  };

  const handleDelete = async (poNumber) => {
    if (window.confirm(`Are you sure you want to delete PO ${poNumber}?`)) {
      try {
        const response = await fetch(`${API_URL}/purchase-order/${poNumber}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (!response.ok) throw new Error('Failed to delete purchase order');

        // Refresh the list
        fetchPurchaseOrders(currentPage); // Keep current page
        alert('Purchase order deleted successfully');
      } catch (error) {
        console.error('Error deleting purchase order:', error);
        alert('Cannot delete purchase order due to items are linked to mrn items');
      }
    }
  };

  const handleModalSubmit = () => {
    // Refresh the list after form submission
    fetchPurchaseOrders(currentPage); // Keep current page
  };

  // Toggle PO active status
  const togglePOStatus = async (poNumber, currentStatus) => {
    try {
      const newStatus = currentStatus ? 0 : 1;
      const endpoint = newStatus ? 'approve' : 'pending';

      const response = await fetch(`${API_URL}/purchase-order/${poNumber}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) throw new Error(`Failed to ${endpoint} purchase order`);

      // Refresh the list
      fetchPurchaseOrders(currentPage); // Keep current page
      alert(`Purchase order ${endpoint}d successfully`);
    } catch (error) {
      console.error(`Error updating purchase order status:`, error);
      alert(`Error updating purchase order status`);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    // Fetching is now handled by useEffect watching sortConfig
  };

  // Get sorted data (Function kept but logic moved to server)
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      // Handle different data types
      switch (sortConfig.key) {
        case 'PurchaseOrderNumber':
        case 'vendor_name':
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
          break;
        case 'PurchaseOrderDate':
          aValue = new Date(a[sortConfig.key]);
          bValue = new Date(b[sortConfig.key]);
          break;
       case 'LocationCode':
        case 'BranchCode':
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
          break;
        case 'ProjectCode':
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
          break;
        case 'is_active':
          aValue = a[sortConfig.key] ? 1 : 0;
          bValue = b[sortConfig.key] ? 1 : 0;
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }

      // Compare values
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filtering logic moved to server. Local variables kept for compatibility but unused in render.
  const filteredPOs = purchaseOrders; 
  const sortedPOs = purchaseOrders; 

  // Debounced Effect for Search, Status, and Sort changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      // Reset to page 1 when search or sort changes
      setCurrentPage(1); 
      fetchPurchaseOrders(1);
    }, 500); // 500ms delay to prevent spamming API while typing

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, status, sortConfig]);

  // Effect for Page Changes
  useEffect(() => {
    // Fetch when currentPage changes (unless it's the initial mount reset from search)
    if (currentPage > 0) {
        fetchPurchaseOrders(currentPage);
    }
  }, [currentPage]);

  // Pagination logic is now handled by server, but we keep startIndex/EndIndex if you use them for UI
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Current items are just the purchaseOrders state (server handles the limit)
  const currentItems = purchaseOrders;

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Helper function to render sort arrows
  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';

    return (
      <span className="ml-2 flex flex-col">
        <FaSortUp className={`h-3 w-3 ${isAsc ? 'text-slate-800' : 'text-slate-400'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1.5 ${isDesc ? 'text-slate-800' : 'text-slate-400'}`} />
      </span>
    );
  };

  // Add this debugging code to verify pagination is working
  useEffect(() => {
    console.log('Current page:', currentPage);
    console.log('Items per page:', itemsPerPage);
    console.log('Total items:', sortedPOs.length);
    console.log('Current items:', currentItems.length);
  }, [currentPage, sortedPOs, currentItems]);

  // Render pagination buttons (match Sales Order style)
  const renderPaginationButtons = () => {
    const buttons = [];
    // Use totalPages from state
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    buttons.push(
      <button
        key="prev"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${
          currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'
        } rounded-none`} /* SHARP edges */
      >
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-4 py-1 font-medium transition-all duration-200 ${
            i === currentPage
              ? 'bg-blue-500 text-white rounded-none' /* SHARP */
              : 'text-slate-800 hover:bg-slate-100 rounded-none' /* SHARP */
          }`}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all duration-200 ${
          currentPage === totalPages || totalPages === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'
        } rounded-none`} /* SHARP edges */
      >
        <FaChevronRight className="h-5 w-5" />
      </button>
    );

    return buttons;
  };

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('MRN_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MRN_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MRN_MASTER', 'CanDelete');
 
  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: COLORS.ANGEL }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-slate-800 mx-auto rounded-full"></div>
          <p className="mt-4 text-lg font-medium text-slate-700">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex"
      style={{
        backgroundColor: COLORS.ANGEL,
        // CSS variables read by Sidebar to pick up page theme while sidebar background stays white
        '--accent-color': '#0f766e',        // Purchase Order active item color (teal)
        '--accent-hover': '#2b8f85',
        '--hover-gray': '#f1f5f9',
        '--page-border-color': COLORS.DIVIDER,
        '--sidebar-bg': '#ffffff',
        '--page-background': COLORS.ANGEL
      }}
    >
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ activeBg: '#4f8ef5ff', activeText: '#ffffffff', profileBg: '#4f8ef5ff' }}
        onLogout={handleLogout}
      />
      <main
        className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}
      >
        {/* Header */}
      <header>
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-dark-500 mb-2">
                Purchase Orders
              </h1>
              <p className="text-gray-600">
                Manage your purchase orders here.
              </p>
            </div>
            <div className="flex items-center gap-3">
               <button 
                onClick={handleSync} 
                disabled={isSyncing}
                className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-all duration-200 border ${
                  isSyncing 
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300' 
                    : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
                }`}
              >
                {isSyncing ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Syncing...
                  </>
                ) : (
                  <>
                    <FaCheck className="h-4 w-4" />
                    Sync 
                  </>
                )}
              </button>

            <button onClick={handleDownloadCSV} className="flex items-center gap-2 rounded-none border border-slate-200 px-3 py-2 bg-white text-slate-800 hover:bg-slate-50 transition-all duration-200"><FaDownload className="w-4 h-4" />Export</button>
            {/* <button onClick={handleCreate} className="flex items-center gap-2 rounded-none bg-blue-500 text-white px-3 py-2 hover:bg-blue-400 transition-all duration-200"><FaPlus className="w-4 h-4" />Create New</button> */}
            {/* <button onClick={() => { if (currentItems[0]) { setSelectedPOForPDF(currentItems[0]); setIsPDFDesignerOpen(true); } }} className="flex items-center gap-2 rounded-none px-3 py-2 bg-red-500 text-white hover:bg-red-600 transition-all duration-200">
              <FaEdit className="w-4 h-4" />Design PDF
            </button> */}
          </div>
          </div>
          {/* Divider: matches stockout/department style and sits below header text and above stats cards */}
          <div style={{ height: 1, backgroundColor: COLORS.DIVIDER, marginTop: 16, marginBottom: 20 }} />
          </div>
        </header>

        {/* Stats Cards */}
        {/* <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total POs</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.totalPOs}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V4a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Approved POs</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.approvedPOs}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 text-teal-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Pending POs</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.pendingPOs}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-50 text-amber-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Created This Month</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.createdThisMonth}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </section> */}

        {/* Search & Filter */}
        <section className="p-5 bg-white border border-slate-200 shadow-sm mb-6 rounded-none"> {/* SHARP */}
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search PO number, vendor, etc."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none" /* SHARP */
              />
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none" 
              >
                <option value="All">All</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
              </select>
            </div> */}
            <button
              className="px-5 py-2 bg-blue-500 text-white font-medium hover:bg-blue-400 transition-all duration-200 shadow rounded-none" /* SHARP */
              onClick={() => fetchPurchaseOrders(1)}
            >
              Search
            </button>
          </div>
        </section>

        {/* Table */}
        <section className="shadow-sm overflow-hidden rounded-none border border-slate-200"> {/* SHARP container */}
          {/* Section title block — now WHITE block as you asked */}
          <div
            className="flex justify-between items-center p-4"
            style={{ backgroundColor: COLORS.SECTION_TITLE_BG, borderBottom: `1px solid ${COLORS.DIVIDER}` }} /* added divider like stockout */
          >
            <h2 className="text-xl font-semibold" style={{ color: COLORS.SECTION_TITLE_TEXT }}>
              Purchase Orders
            </h2>
            <div className="flex gap-2">{renderPaginationButtons()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-600">Loading purchase orders...</div>
          ) : sortedPOs.length === 0 ? (
            <div className="p-8 text-center text-slate-600">No purchase orders found</div>
          ) : (
            <div className="overflow-x-auto" style={{ backgroundColor: COLORS.TABLE_BODY }}>
              <table className="w-full text-sm" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                {/* Thead keeps THALASSA background with white header text */}
                <thead style={{ backgroundColor: COLORS.THALASSA }} classname="text-gray-600 text-xs text-left font-semibold uppercase">
                  <tr>
                    <th
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200 text-gray-600"
                      onClick={() => handleSort('PurchaseOrderNumber')}
                      
                    >
                      <div className="flex items-center">
                        PO Number
                        {renderSortArrows('PurchaseOrderNumber')}
                      </div>
                    </th>
                    {/* <th
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200 text-gray-600"
                      onClick={() => handleSort('PurchaseOrderDate')}
                      
                    >
                      <div className="flex items-center">
                        PO Date
                        {renderSortArrows('PurchaseOrderDate')}
                      </div>
                    </th> */}
                    <th
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200 text-gray-600"
                      onClick={() => handleSort('vendor_name')}
                   
                    >
                      <div className="flex items-center">
                        Vendor Name
                        {renderSortArrows('vendor_name')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200 text-gray-600"
                      onClick={() => handleSort('LocationCode')}
                    >
                      <div className="flex items-center">
                        Location Code
                        {renderSortArrows('LocationCode')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200 text-gray-600"
                      onClick={() => handleSort('BranchCode')}
                    >
                      <div className="flex items-center">
                        Branch Code
                        {renderSortArrows('BranchCode')}
                      </div>
                    </th>
                     <th
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200 text-gray-600"
                      onClick={() => handleSort('ProjectCode')}
                    >
                      <div className="flex items-center">
                        Project Code
                        {renderSortArrows('ProjectCode')}
                      </div>
                    </th>

                    {/* <th
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200 text-gray-600"
                      onClick={() => handleSort('is_active')}
                     
                    >
                      <div className="flex items-center">
                        Status
                        {renderSortArrows('is_active')}
                      </div>
                    </th> */}
                    <th className="px-6 py-3 text-center text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>

                {/* tbody is white; dividers use DIVIDER color */}
                <tbody className="divide-y" style={{ borderColor: COLORS.DIVIDER }}>
                                    {currentItems.map((po) => {
                    
                    const totalItems = po.TotalItems || 0; 
                    const totalQuantity = po.TotalQuantity || 0;
                    const totalAmount = po.TotalAmount || 0;
                    const totalRemainingQty = po.TotalRemainingQty || 0;
                    
                    const isActive = po.is_active === 1 || po.is_active === true;
                    const statusText = isActive ? 'Approved' : 'Pending';

                    return (
                      <tr key={po.PurchaseOrderNumber} className="hover:bg-white/70 transition-colors duration-200">
                        <td className="px-6 py-3 text-left font-medium text-slate-900" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          {po.PurchaseOrderNumber}
                        </td>
                        {/* <td className="px-6 py-3 text-left  text-slate-700" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          {new Date(po.PurchaseOrderDate).toLocaleDateString()}
                        </td> */}
                        <td className="px-6 py-3 text-left text-slate-700 uppercase" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          {po.vendor_name || '-'}
                        </td>
                        <td className="px-6 py-3 text-left text-slate-700 uppercase" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          {po.LocationCode || '-'}
                        </td>
                        <td className="px-6 py-3 text-left text-slate-700 uppercase" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          {po.BranchCode || '-'}
                        </td>
                         <td className="px-6 py-3 text-left text-slate-700 uppercase" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          {po.ProjectCode || '-'}
                        </td>
                        {/* <td className="px-6 py-3" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          <span
                            className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer transition-colors duration-200 ${
                              isActive ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                            } rounded-full`}
                            onClick={() => togglePOStatus(po.PurchaseOrderNumber, isActive)}
                            title={`Click to ${isActive ? 'set as pending' : 'approve'}`}
                          >
                            {statusText}
                          </span>
                        </td> */}
                        <td className="px-6 py-3" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                          <div className="flex gap-3 justify-center">
                            {canView && (
                              <button
                                onClick={() => handleView(po)}
                                className="text-slate-800 hover:text-slate-900 transition-colors duration-200 rounded-none p-1" /* SHARP */
                                title="View"
                              >
                                <FaEye className="h-4 w-4" />
                              </button>
                            )}
                            {/* {canEdit && (
                              <button
                                onClick={() => handleEdit(po)}
                                className="text-teal-700 hover:text-teal-900 transition-colors duration-200 rounded-none p-1" 
                                title="Edit"
                              >
                                <FaEdit className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(po.PurchaseOrderNumber)}
                                className="text-rose-600 hover:text-rose-800 transition-colors duration-200 rounded-none p-1" 
                                title="Delete"
                              >
                                <FaTrash className="h-4 w-4" />
                              </button>
                            )} */}
                           
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Purchase Order Form Modal */}
        <PurchaseOrderFormModal open={showModal} onClose={() => setShowModal(false)} onSubmit={handleModalSubmit} initialData={selectedPO} isEdit={!!selectedPO} />

        <PurchaseOrderViewModal open={viewModalOpen} onClose={() => setViewModalOpen(false)} poId={selectedPO} />
        {/* PDF Designer Modal */}
        {isPDFDesignerOpen && selectedPOForPDF && (
          <PDFDesigner
            isOpen={isPDFDesignerOpen}
            onClose={() => setIsPDFDesignerOpen(false)}
            entity="Purchase Order"
            sampleData={{
              PONumber: selectedPOForPDF.PurchaseOrderNumber,
              PODate: new Date(selectedPOForPDF.PurchaseOrderDate).toLocaleDateString(),
              VendorCode: selectedPOForPDF.vendor_code || '-',
              VendorName: selectedPOForPDF.vendor_name || '-',
              Branch: selectedPOForPDF.Branch || '-',
              SupplierContact: selectedPOForPDF.SupplierContact || '-',
              VendorAddress: selectedPOForPDF.VendorAddress || '-',
              GSTNumber: selectedPOForPDF.GSTNumber || '-',
              DeliveryDate: selectedPOForPDF.DeliveryDate || '-',
              Currency: selectedPOForPDF.Currency || 'INR',
              PaymentTerms: selectedPOForPDF.PaymentTerms || '-',
              Warehouse: selectedPOForPDF.Warehouse || '-',
              Status: selectedPOForPDF.is_active ? 'Approved' : 'Pending',
              Remarks: selectedPOForPDF.Remarks || '-',
              Items: (selectedPOForPDF.Items || []).map((item) => ({
                ItemCode: item.ItemCode,
                ProductName: item.ItemName,
                Description: item.Description || '-',
                UOM: item.UOM,
                Quantity: item.Quantity,
                RemainingQty: item.RemainingQty,
                UnitPrice: item.UnitPrice,
                Discount: item.Discount,
                Tax: item.Tax,
                TaxRate: item.TaxRate,
                TaxAmount: item.TaxAmount,
                Total: item.Total,
              })),
              Summary: {
                TotalItems: selectedPOForPDF.Items?.length || 0,
                TotalQuantity: selectedPOForPDF.Items?.reduce((sum, i) => sum + (i.Quantity || 0), 0),
                TotalRemainingQty: selectedPOForPDF.Items?.reduce((sum, i) => sum + (i.RemainingQty || 0), 0),
                GrandTotal: selectedPOForPDF.Items?.reduce((sum, i) => sum + (i.Total || 0), 0),
              },
            }}
            availableFields={{
              Header: [
    "PONumber", "PODate", "VendorCode", "VendorName", "Branch",
      
    "SupplierContact", "VendorContactEmail", "VendorContactPhone", "VendorContactCode",
    "VendorAddress", "GSTNumber", "DeliveryDate",
    "Currency", "PaymentTerms", "Warehouse", "Status", "Remarks"
  ],
               Items: [
    "ItemCode", "ProductCode", "ProductName", "Description", "UOM", "Quantity",
    "RemainingQty", "UnitPrice", "Discount", "Tax", "TaxRate", "TaxAmount", 
    "DepartmentCode", "Total"
  ],
              Summary: ['TotalItems', 'TotalQuantity', 'TotalRemainingQty', 'GrandTotal'],
            }}
          />
        )}

         {showSyncSuccess && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center transform transition-all scale-100">
      {/* Success Icon */}
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
        <FaCheck className="h-8 w-8 text-green-600" />
      </div>

      <h3 className="text-2xl font-bold text-gray-900 mb-2">Sync Complete!</h3>
      <p className="text-gray-500 mb-4">
        Purchase order data has been successfully synchronized.
      </p>

      {skippedItems.length > 0 && (
        <div className="text-left mb-4">
          <button
            onClick={() => setShowSkippedDetails((prev) => !prev)}
            className="text-blue-600 underline mb-2"
          >
            {showSkippedDetails ? 'Hide' : 'View'} Skipped Items ({skippedItems.length})
          </button>

          {showSkippedDetails && (
            <div className="max-h-64 overflow-y-auto border border-gray-200 p-2 rounded">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1 text-left">Item Code</th>
                    <th className="px-2 py-1 text-left">Location</th>
                    <th className="px-2 py-1 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {skippedItems.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-2 py-1">{item.ItemCode}</td>
                      <td className="px-2 py-1">{item.Location || '-'}</td>
                      <td className="px-2 py-1 text-red-600">{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => {
          setShowSyncSuccess(false);
          setShowSkippedDetails(false);
          setSkippedItems([]);
        }}
        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 shadow-sm hover:bg-blue-400 focus:outline-none transition-all duration-200"
      >
        Close
      </button>
    </div>
  </div>
)}


      </main>
    </div>
  );
};

export default PurchaseOrderItems;